import {FrameData} from "./FrameData";

export class CornerDetection {

  private static readonly MAX_SIZE_RATIO = 0.95 ;
  private static readonly MIN_SIZE_RATIO = 0.5;

  private static toGrayscale(frame: FrameData): FrameData {
    for (let i = 0; i < frame.data.length; i += 4) {
      const avg = frame.data[i] * 0.299 + frame.data[i + 1] * 0.587 + frame.data[i + 2] * 0.114;
      frame.data[i] = avg;
      frame.data[i + 1] = avg;
      frame.data[i + 2] = avg;
    }
    return frame;
  }

  private static findBlobsRGBA(frame: FrameData): number[][] {
    const blobs = new Array();
    let blobIndex = 0;
    const stack = new Array();
    let searching = true;
    for (let i = 0; i < frame.data.length; i += 4) {
      if (frame.data[i] === 255) {  // If a white pixel, it hasn't been added to a blob.
        searching = true;
        frame.data[i] = 0;
        stack[0] = i;
        blobs[blobIndex] = [i];
        let currentPixel = i;
        while (searching) {
          if (frame.data[currentPixel - 4] === 255) {
            frame.data[currentPixel - 4] = 0;
            blobs[blobIndex].push(currentPixel - 4);
            stack.push(currentPixel - 4);
          }
          if (frame.data[currentPixel + 4] === 255) {
            frame.data[currentPixel + 4] = 0;
            blobs[blobIndex].push(currentPixel + 4);
            stack.push(currentPixel + 4);
          }
          if (frame.data[currentPixel - frame.width * 4] === 255) {
            frame.data[currentPixel - frame.width * 4] = 0;
            blobs[blobIndex].push(currentPixel - frame.width * 4);
            stack.push(currentPixel - frame.width * 4);

          }
          if (frame.data[currentPixel + frame.width * 4] === 255) {
            frame.data[currentPixel + frame.width * 4] = 0;
            blobs[blobIndex].push(currentPixel + frame.width * 4);
            stack.push(currentPixel + frame.width * 4);
          }
          if (stack.length === 0) {
            searching = false;
            blobIndex++;
          } else {
            currentPixel = stack.pop();
          }
        }
      }
    }
    return blobs;
  }

  private static getLargestBlobRGBA(blobs: number[][]) {
    let largestIndex = 0;
    for (let i = 0; i < blobs.length; i++) {
      if (blobs[i].length > blobs[largestIndex].length) {
        largestIndex = i;
      }
    }
    return blobs[largestIndex];
  }

  private static minBoundingCornersRGBA(blob: number[], width: number) {
    let xMin = Number.POSITIVE_INFINITY;
    let xMax = 0;
    let yMin = Number.POSITIVE_INFINITY;
    let yMax = 0;
    const coordinates = [];
    for (const pixel of blob) {
      const x = (pixel / 4) % (width);
      const y = Math.round(pixel / (4 * width));
      coordinates.push([x, y]);
      if (xMin > x) {xMin = x; }
      if (xMax < x) {xMax = x; }
      if (yMin > y) {yMin = y; }
      if (yMax < y) {yMax = y; }
    }
    const ratio = (xMax - xMin) / (yMax - yMin);
    if ((ratio > 1.6 && ratio < 1.65) || (ratio > 0.6 && ratio < 0.65)) {
        return [[xMin, yMin], [xMax, yMin], [xMax, yMax], [xMin, yMax]];
    }

    let xMinyMin = Number.POSITIVE_INFINITY;
    let xMaxyMin = Number.POSITIVE_INFINITY;
    let yMinxMax = 0;
    let yMaxMin = Number.POSITIVE_INFINITY;
    for (const pixel of coordinates) {
      if (pixel[0] === xMin) {
        if (pixel[1] <= xMinyMin) {xMinyMin = pixel[1]; }
      }
      if (pixel[0] === xMax) {
        if (pixel[1] <= xMaxyMin) {xMaxyMin = pixel[1]; }
      }
      if (pixel[1] === yMin) {
        if (pixel[0] >= yMinxMax) {yMinxMax = pixel[0]; }
      }
      if (pixel[1] === yMax) {
        if (pixel[0] <= yMaxMin) {yMaxMin = pixel[0]; }
      }
    }
    return [[xMin, xMinyMin], [yMinxMax, yMin], [xMax, xMaxyMin], [yMaxMin, yMax]];
  }

  private static isDriverLicense(corners: number[][], width: number, height: number): boolean {
    const edge1 = this.euclideanDistance(corners[0][0], corners[0][1], corners[1][0], corners[1][1]);
    const edge2 = this.euclideanDistance(corners[1][0], corners[1][1], corners[2][0], corners[2][1]);
    const edge3 = this.euclideanDistance(corners[2][0], corners[2][1], corners[3][0], corners[3][1]);
    const edge4 = this.euclideanDistance(corners[3][0], corners[3][1], corners[0][0], corners[0][1]);
    const ratios: number[] = [(edge1 / edge2), (edge1 / edge4), (edge3 / edge2), (edge3 / edge4)];

    for (const ratio of ratios) {
      if (!(ratio > 1.6 && ratio < 1.65) && !(ratio > 0.6 && ratio < 0.65)) {
        return false;
      }
    }

    const imageLongEdge = Math.max(width, height);
    const imageShortEdge = Math.min(width, height);
    const longEdge = Math.max(edge1, edge2);
    const shortEdge = Math.min(edge1, edge2);

    // Check not too small/large
    if ((longEdge > (this.MAX_SIZE_RATIO * imageLongEdge)) || (longEdge < (this.MIN_SIZE_RATIO * imageLongEdge))) {
      return false;
    }
    if ((shortEdge > (this.MAX_SIZE_RATIO * imageShortEdge)) || (shortEdge < (this.MIN_SIZE_RATIO * imageShortEdge))) {
      return false;
    }

    // Corners on edge of frame
    let count = 0;
    for (const corner of corners) {
      if (corner[0] === 0 || corner[0] === width) {
        count++;
      }
      if (corner[1] === 0 || corner[1] === height) {
        count++;
      }
    }
    if (count >= 4) {
      return false;
    }
    return true;
  }

  private static euclideanDistance(x1: number, y1: number, x2: number, y2: number): number {
    return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
  }

  private static adaptiveThreshold(frame: FrameData, guess = 120): [boolean, number[][], number, boolean] {
    const copy = new FrameData(frame.data.slice(), frame.width, frame.height);
    for (let i = 0; i < 255; i += 10) {
      copy.data.set(frame.data);
      let result = this.determineGoodThreshold(copy, guess + i);
      if (result[0]) {
        return result;
      }
      copy.data.set(frame.data);
      result = this.determineGoodThreshold(copy, guess + i, true);
      if (result[0]) {
        return result;
      }
      copy.data.set(frame.data);
      result = this.determineGoodThreshold(copy, guess - i);
      if (result[0]) {
        return result;
      }
      copy.data.set(frame.data);
      result = this.determineGoodThreshold(copy, guess - i, true);
      if (result[0]) {
        return result;
      }
    }
    return [false, [], guess, false];
  }

  private static determineGoodThreshold(
    frame: FrameData,
    threshold: number,
    INV_BINARY = false,
  ): [boolean, number[][], number, boolean] {
    const result = this.threshold(frame, threshold, INV_BINARY);
    const blobs = this.findBlobsRGBA(result);
    if (blobs.length > 0) {
      const corners = this.minBoundingCornersRGBA(this.getLargestBlobRGBA(blobs), result.width);
      if (this.isDriverLicense(corners, frame.width, frame.height)) {
        return [true, corners, threshold, INV_BINARY];
      }
    }
    return [false, [], threshold, INV_BINARY];
  }

  private static threshold(frame: FrameData, threshold: number, INV_BINARY = false): ImageData {
    for (let i = 0; i < frame.data.length; i += 4) {
      const pixel = frame.data[i];
      let color = 0;
      if (pixel > threshold && INV_BINARY === false) {
        color = 255;
      } else if (pixel < threshold && INV_BINARY === true) {
        color = 255;
      }
      frame.data[i] = color;
      frame.data[i + 1] = color;
      frame.data[i + 2] = color;
    }
    return frame;
  }

 public static driverLicenseIsInFrame(
   data: Uint8ClampedArray,
   width: number,
   height: number,
   threshold = 120,
   failedFrames = 0,
   INV_BINARY  = false,
 ): [boolean, number[][], number, boolean] {
    let frame = new FrameData(data, width, height);
    frame = this.toGrayscale(frame);
    if (failedFrames === 0 || failedFrames % 20 === 0) {
      return this.adaptiveThreshold(frame);
    }
    return this.determineGoodThreshold(frame, threshold, INV_BINARY);
  }
}



// WEBPACK FOOTER //
// ./src/constants/CornerDetection.ts